import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Trash2, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const thisYear = new Date().getFullYear();

// Public holidays: clock-in reminders and lateness skip these dates.
export const HolidaysEditor = () => {
    const [year, setYear] = useState(thisYear);
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ date: "", name: "" });
    const [open, setOpen] = useState(false);

    const load = useCallback(() => api.get("/holidays", { params: { year } }).then(({ data }) => setItems(data || [])).catch(() => {}), [year]);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!form.date || !form.name.trim()) return toast.error("Enter a date and a name");
        try {
            await api.post("/holidays", form);
            const y = Number(form.date.slice(0, 4));
            setForm({ date: "", name: "" });
            if (y !== year && y >= thisYear - 1 && y <= thisYear + 1) setYear(y);
            else load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not add holiday");
        }
    };
    const remove = async (h) => { await api.delete(`/holidays/${h.id}`).catch(() => {}); load(); };
    const preset = async () => {
        try {
            const { data } = await api.post("/holidays/load-nigeria", null, { params: { year } });
            toast.success(`Added ${data.added} Nigerian public holidays`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No preset for this year");
        }
    };

    return (
        <section className="mt-6 bg-white border border-ink/10 p-6 sm:p-8" data-testid="settings-holidays">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="overline">Attendance</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink flex items-center gap-2"><CalendarDays className="w-5 h-5 text-klein" /> Public holidays</h2>
                    <p className="mt-1 text-sm text-ink/60">No clock-in reminders or lateness on these days. Islamic holidays are estimates — adjust when the government announces dates.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-ink/15 px-3 py-2 text-sm" data-testid="holidays-year">
                        {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button type="button" onClick={preset} className="inline-flex items-center gap-2 border border-ink/20 px-3 py-2 text-sm hover:bg-ink/5" data-testid="holidays-load-preset">
                        <Download className="w-4 h-4" /> Add Nigerian holidays
                    </button>
                </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <button type="button" className="inline-flex items-center gap-2 border border-ink/15 px-3 py-2 text-sm min-w-[170px] text-left hover:bg-ink/5" data-testid="holiday-date-input">
                            <CalendarDays className="w-4 h-4 text-ink/50" />
                            <span className={form.date ? "text-ink" : "text-ink/40"}>{form.date ? format(parseISO(form.date), "EEE, d MMM yyyy") : "Pick a date"}</span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={form.date ? parseISO(form.date) : undefined} defaultMonth={form.date ? parseISO(form.date) : new Date(year, new Date().getMonth())}
                            onSelect={(d) => { if (d) { setForm({ ...form, date: format(d, "yyyy-MM-dd") }); setOpen(false); } }} initialFocus data-testid="holiday-date-calendar" />
                    </PopoverContent>
                </Popover>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Company retreat" className="flex-1 min-w-[180px] border border-ink/15 px-3 py-2 text-sm" data-testid="holiday-name-input" />
                <button type="button" onClick={add} className="inline-flex items-center gap-1.5 bg-klein text-white px-4 py-2 text-sm" data-testid="holiday-add-button"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <ul className="mt-4 divide-y divide-ink/5" data-testid="holidays-list">
                {items.length === 0 && <li className="py-6 text-sm text-ink/40 text-center">No holidays for {year} yet.</li>}
                {items.map((h) => (
                    <li key={h.id} className="flex items-center gap-3 py-2.5" data-testid={`holiday-row-${h.date}`}>
                        <span className="w-28 text-sm tabular-nums text-ink/70">{new Date(`${h.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
                        <span className="flex-1 text-sm text-ink">{h.name}</span>
                        <button type="button" onClick={() => remove(h)} className="p-1.5 text-ink/40 hover:text-red-600" data-testid={`holiday-delete-${h.date}`}><Trash2 className="w-4 h-4" /></button>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default HolidaysEditor;
